import * as Sentry from '@sentry/nextjs';
import { filesCreateUpload, filesFinishUpload } from '@/app/apis';
import { ACCEPT_VIDEO_MIMETYPE, UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import type { ServiceError } from '@/app/types';
import { getLoginUrl } from '../../get-login-url';
import { createImage } from '../../image-utils';
import { openOrRedirect } from '../../open-or-redirect';
import { compressImageIfNeeded } from '../compress-image-if-needed';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB per chunk
const MAX_IMAGE_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB - 仅用于图片压缩限制
const MAX_IMAGE_AREA = 6000 * 6000; // 数美审核的图片像素限制

const isSubtitleFile = (file: Blob): file is File => {
  return file instanceof File && file.name.toLowerCase().endsWith('.srt');
};

// 获取网络状态信息,但仅包含下行速度,无法获取上行速度
// networkType: 网络类型(wifi/4g等)
// effectiveType: 实际网络类型(4g/3g/2g等)
// downlink: 下行速度(Mbps)
// rtt: 网络延迟(ms)
// saveData: 是否开启数据节省模式
// 注意:这些是设备整体网络状态,不能反映与特定服务器的实际连接质量
const getNetworkInfo = () => {
  if ('connection' in navigator) {
    const connection = (navigator as { connection?: Record<string, unknown> }).connection;
    return {
      networkType: (connection?.type as string) || 'unknown',
      effectiveType: (connection?.effectiveType as string) || 'unknown',
      downlink: (connection?.downlink as number) || 0,
      rtt: (connection?.rtt as number) || 0,
      saveData: (connection?.saveData as boolean) || false,
    };
  }
  return {
    networkType: 'unknown',
    effectiveType: 'unknown',
    downlink: 0,
    rtt: 0,
    saveData: false,
  };
};

export interface UploadResult {
  id?: string;
  uri: string;
  file: Blob;
  width?: number; // 实际上传的图片宽度（如果进行了压缩，则为压缩后的宽度）
  height?: number; // 实际上传的图片高度（如果进行了压缩，则为压缩后的高度）
  cancelled?: boolean;
  error?: ServiceError;
}

const handleUnauthorizedError = (error: ServiceError) => {
  if (error.reason === 'Unauthorized' || error.code === 401) {
    const loginUrl = getLoginUrl(window.location.pathname);
    openOrRedirect(loginUrl, {
      target: '_self',
    });
  }
};

/**
 * 上传文件，支持大文件分块上传，支持传入 AbortSignal 取消请求
 *
 * @param file 文件
 * @param width 宽度
 * @param height 高度
 * @param scene 场景
 * @param abortController 取消请求
 * @returns 上传结果
 */
export const uploadFileInternal = async ({
  file: originalFile,
  accessToken,
  width: originalWidth = 0,
  height: originalHeight = 0,
  duration,
  scene = UPLOAD_SCENE.VIDU,
  videoAccept = ACCEPT_VIDEO_MIMETYPE,
  abortController,
  autoPart = false,
}: {
  file: Blob;
  accessToken?: string;
  width?: number;
  height?: number;
  duration?: number;
  scene?: UploadScene;
  videoAccept?: string[];
  abortController?: AbortController;
  /** 是否自动分片上传 */
  autoPart?: boolean;
}): Promise<UploadResult> => {
  // 只对图片类型进行压缩检查，视频类型（如 mp4/mov/webm）允许超过 10MB，不进行压缩。
  // 压缩主流程已下沉到 `compressImageIfNeeded`，与 claw 上传链路共享同一份实现。
  let file = originalFile;
  let width = originalWidth;
  let height = originalHeight;

  // 读尺寸失败要上报 Sentry（保留原行为）；读到 0 让 compressImageIfNeeded 自己再尝试。
  if ((width === 0 || height === 0) && originalFile.type.startsWith('image/')) {
    try {
      const imageUrl = URL.createObjectURL(originalFile);
      try {
        const image = await createImage(imageUrl);
        width = image?.width ?? 0;
        height = image?.height ?? 0;
      } finally {
        URL.revokeObjectURL(imageUrl);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          'upload.stage': 'read_image_dimensions',
          'upload.error_type': 'image_read_failed',
        },
        level: 'error',
      });
    }
  }

  const compressOutcome = await compressImageIfNeeded(originalFile, {
    maxUploadSize: MAX_IMAGE_UPLOAD_SIZE,
    maxArea: MAX_IMAGE_AREA,
    width,
    height,
  });

  if (compressOutcome.ok) {
    file = compressOutcome.file;
    width = compressOutcome.width;
    height = compressOutcome.height;
  } else {
    const { exceedsSize, exceedsArea } = compressOutcome;
    const errorMessage =
      exceedsSize && exceedsArea
        ? 'Failed to compress image to within 10MB and 8k resolution'
        : exceedsSize
          ? 'Failed to compress image to within 10MB'
          : 'Failed to resize image to within 8k resolution';
    Sentry.setContext('file_upload', {
      fileSize: originalFile.size,
      originalFileSize: originalFile.size,
      fileType: originalFile.type,
      originalFileType: originalFile.type,
      width,
      originalWidth,
      height,
      originalHeight,
      scene,
      ...getNetworkInfo(),
    });
    Sentry.captureException(new Error(errorMessage), {
      tags: {
        'upload.stage': 'compress',
        'upload.error_type': 'image_compression_failed',
      },
      level: 'error',
    });
    return {
      uri: '',
      file: originalFile,
      error: {
        reason: 'ImageCompressionFailed',
        message: errorMessage,
        code: 400,
      },
    };
  }

  // 设置 Sentry 上下文，用于所有相关的错误报告
  Sentry.setContext('file_upload', {
    fileSize: file.size,
    originalFileSize: originalFile.size,
    fileType: file.type,
    originalFileType: originalFile.type,
    width,
    originalWidth,
    height,
    originalHeight,
    scene,
    ...getNetworkInfo(),
  });

  // 记录上传开始
  Sentry.addBreadcrumb({
    message: 'File upload started',
    category: 'file.upload',
    level: 'info',
    data: {
      fileSize: file.size,
      originalFileSize: originalFile.size,
      fileType: file.type,
      originalFileType: originalFile.type,
      scene,
      compressed: originalFile.size !== file.size,
    },
  });

  try {
    // 如果 autoPart 为 true，则自动分片上传，否则仅视频类型支持分片上传
    const isVideoFile = videoAccept.includes(file.type);
    const isAudioFile = file.type.includes('audio');
    const partCount = autoPart || isVideoFile ? Math.ceil(file.size / CHUNK_SIZE) : 1;
    const isMultipart = partCount > 1;

    const widthStr = width.toString();
    const heightStr = height.toString();
    const durationMetadata: Record<string, string> = duration !== undefined ? { duration: duration.toString() } : {};

    // 阶段 1: 创建上传任务
    Sentry.addBreadcrumb({
      message: 'Creating upload task',
      category: 'file.upload.create',
      level: 'info',
      data: { partCount },
    });

    const { data: createData, error: createError } = await filesCreateUpload({
      body: {
        metadata: isVideoFile
          ? {
              'image-height': heightStr,
              'image-width': widthStr,
              ...(isMultipart ? { 'Content-Type': file.type } : {}),
              ...durationMetadata,
            }
          : isAudioFile
            ? durationMetadata
            : isSubtitleFile(file)
              ? {}
              : {
                  'image-height': heightStr,
                  'image-width': widthStr,
                },
        part_count: partCount > 1 ? partCount : undefined,
        scene,
      },
      ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
      signal: abortController?.signal,
    }).catch((error) => {
      // 用户取消不作为错误上报
      if (error.name !== 'AbortError') {
        Sentry.captureException(error, {
          tags: {
            'upload.stage': 'create',
            'upload.error_type': 'network_error',
          },
          level: 'error',
        });
      }
      throw error;
    });

    // 业务报错处理
    if (createError) {
      handleUnauthorizedError(createError);
      // 记录创建阶段的错误
      Sentry.captureException(createError, {
        tags: {
          'upload.stage': 'create',
          'upload.error_type': createError.reason,
        },
        level: 'error',
      });
      // 直接返回 ServiceError
      return {
        uri: '',
        file,
        error: createError,
      };
    }

    const { put_urls, put_url, id: uploadId, headers: signedHeaders } = createData;

    if (!put_urls?.length && !put_url) {
      const error = new Error('No put_urls received from server');
      Sentry.captureException(error, {
        tags: {
          'upload.stage': 'create',
          'upload.error_type': 'no_put_urls',
        },
        level: 'error',
      });
      return {
        uri: '',
        file,
        error: {
          reason: 'ServerError',
          message: error.message,
          code: 500,
        },
      };
    }

    // 如果 put_urls 为空，则使用 put_url
    const uploadUrls = put_urls?.length ? put_urls : [put_url];

    // 阶段 2: 上传文件分片
    Sentry.addBreadcrumb({
      message: `Uploading ${uploadUrls.length} chunk(s)`,
      category: 'file.upload.chunks',
      level: 'info',
      data: {
        uploadId,
        chunksCount: uploadUrls.length,
        totalSize: file.size,
      },
    });

    let etags: string[];
    try {
      etags = await Promise.all(
        uploadUrls.map(async (put_url: string, index: number) => {
          // 计算当前分片的起始和结束位置
          const start = index * CHUNK_SIZE;
          const end = Math.min(file.size, start + CHUNK_SIZE);
          // 使用slice方法获取文件分片
          const chunk = partCount === 1 ? file : file.slice(start, end);

          const response = await fetch(put_url, {
            body: chunk,
            headers: isVideoFile
              ? partCount === 1
                ? signedHeaders
                : undefined
              : isAudioFile
                ? signedHeaders
                : isSubtitleFile(file)
                  ? {
                      // 字幕文件的 Content-Type 大概率没有类型，固定传 application/x-subrip
                      'Content-Type': 'application/x-subrip',
                    }
                  : signedHeaders,
            method: 'PUT',
            signal: abortController?.signal,
          });

          const etag = response.headers.get('ETag');
          if (!etag) {
            const error = new Error(`No etag received for chunk ${index + 1}`);

            // 上报到 Sentry - ETag 缺失错误
            Sentry.captureException(error, {
              tags: {
                'upload.stage': 'upload',
                'upload.error_type': 'missing_etag',
              },
              level: 'error',
            });

            throw error;
          }

          return etag.replace(/"/g, '');
        }),
      );
    } catch (error) {
      // 如果已经有失败了，则中断其他分片上传
      if (abortController?.signal && !abortController?.signal.aborted) {
        abortController.abort();
      }

      // 处理取消操作
      if (error instanceof Error && error.name === 'AbortError') {
        return { cancelled: true, file, uri: '' };
      }

      // 为分片上传错误添加详细上下文
      if (error instanceof Error && !error.message.startsWith('No etag received')) {
        Sentry.captureException(error, {
          tags: {
            'upload.stage': 'upload',
            'upload.error_type': 'network_error',
          },
          level: 'error',
        });
      }
      return {
        uri: '',
        file,
        error: {
          reason: error instanceof Error ? error.name : 'UnknownError',
          message: error instanceof Error ? error.message : 'Unknown error',
          code: 0,
        },
      };
    }

    if (!etags.length) {
      const error = new Error('Failed to get any valid ETags from uploads');
      Sentry.captureException(error, {
        tags: {
          'upload.stage': 'upload',
          'upload.error_type': 'etags_empty',
        },
        level: 'error',
      });
      return {
        uri: '',
        file,
        error: {
          reason: 'ServerError',
          message: error.message,
          code: 500,
        },
      };
    }

    // 阶段 3: 完成上传
    Sentry.addBreadcrumb({
      message: 'Finishing upload task',
      category: 'file.upload.finish',
      level: 'info',
      data: {
        uploadId,
        etagsCount: etags.length,
      },
    });

    const { data: finishData, error: finishError } = await filesFinishUpload({
      body: {
        etag: etags[0],
        id: uploadId,
        part_etags: partCount > 1 ? etags : undefined,
      },
      path: {
        id: uploadId,
      },
      ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
      signal: abortController?.signal,
    }).catch((error) => {
      // 用户取消不作为错误上报
      if (error.name !== 'AbortError') {
        Sentry.captureException(error, {
          tags: {
            'upload.stage': 'finish',
            'upload.error_type': 'network_error',
          },
          level: 'error',
        });
      }
      throw error;
    });

    if (finishError) {
      handleUnauthorizedError(finishError);

      // 审核失败不作为错误上报
      if (finishError.reason !== 'AuditSubmitIllegal') {
        Sentry.captureException(finishError, {
          tags: {
            'upload.stage': 'finish',
            'upload.error_type': finishError.reason,
          },
          level: 'error',
        });
      }

      // 直接返回 ServiceError
      return {
        uri: '',
        file,
        error: finishError,
      };
    }

    // 记录成功的上传
    Sentry.addBreadcrumb({
      message: 'File upload completed successfully',
      category: 'file.upload.success',
      level: 'info',
    });

    // 操作成功，清理上下文
    Sentry.setContext('file_upload', null);

    return { ...finishData, file, id: uploadId, width, height };
  } catch (error) {
    // 处理取消操作
    if (error instanceof Error && error.name === 'AbortError') {
      Sentry.addBreadcrumb({
        message: 'File upload cancelled by user',
        category: 'file.upload.cancelled',
        level: 'info',
        data: {
          fileSize: file.size,
          originalFileSize: originalFile.size,
          fileType: file.type,
          originalFileType: originalFile.type,
          scene,
        },
      });
      // 取消操作也清理上下文
      Sentry.setContext('file_upload', null);
      return { cancelled: true, file, uri: '', width, height };
    }

    // 错误情况也清理上下文
    Sentry.setContext('file_upload', null);

    return {
      uri: '',
      file,
      error: {
        reason: 'NetworkError',
        message: 'Network error',
        code: 0,
      },
    };
  }
};
